import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { mapAccommodationListResponseForLegacy } from 'component/Accommodation/Card/__helper/mapAccommodationListResponseForLegacy';
import { buildLocationFilterData } from 'component/Search/Location/__helper/locationMapper';
import { LocationFilterData } from 'component/Search/Location/types';

import { Feature } from 'config/features';
import { defaultCountPerPage } from 'config/SearchForm/defaults';
import { useUpdateAccommodation } from 'context/entity/Accommodation/Accommodation';
import { InternalOutputActionContext } from 'context/InternalOutputContext';
import { SearchFormActionContext } from 'context/SearchFormContext';
import { TravelExpertActionContext } from 'context/TravelExpertContext';
import { useSetAtom } from 'jotai';

import { useAddDebugBarEntry } from 'hooks/useDebugBar';
import { useFeatureList } from 'hooks/useFeature';
import { useSaveLastSearch } from 'hooks/useSaveLastSearch';

import { metaAtom } from 'data/Atom/Meta';
import { destinationAtom } from 'data/Atom/Search';
import { searchStore } from 'data/Atom/Store';

import Console from 'utility/Debug/Console';
import {
    CommunicationContext,
    SearchDContext,
} from 'utility/Provide/Context/PageContext';
import {
    CacheHit,
    trackStatsDHotelListCacheHit,
    trackStatsDHotelListTiming,
} from 'utility/Track/statsD';

import { SearchInterface } from 'types/legacy/search';
import { ApiEndpoint } from 'types/Request/endpoint';

import { HotelListResponse } from 'proto/Api/Hotel/HotelList';
import { ResponseStatus } from 'proto/Api/ResponseStatus';
import { HotelList } from 'proto/Hotel/Hotel';
import { MapDataList } from 'proto/MapData/MapData';
import { ActiveFilter } from 'proto/Search/ActiveFilter';
import { FilterInformation } from 'proto/Search/FilterMeta';
import { ServiceInformation } from 'proto/ServiceInformation';
import { Tracking } from 'proto/Tracking/Tracking';

import { HotelListView } from '../component/Accommodation/List/__partials/List/types';
import { useSearch } from '../component/Search/__hooks/useSearch';
import {
    AccommodationListReducer,
    createInitialState,
} from '../reducer/AccommodationList';
import { buildActionMap } from '../reducer/AccommodationList/actionMap';
import {
    ActionMap,
    StartNewSearchScope,
} from '../reducer/AccommodationList/types';

import {
    fetchAccommodationList,
    fetchActiveFilterList,
    fetchChatBotData,
    startPolling,
} from './__helper/fetch';
import {
    getDestinationId,
    trackNoResult,
    trackStatsDHotelList,
} from './__helper/tracking';

const initialState = createInitialState();

const emptyHotelList = HotelList.fromPartial({});

interface HandleSearchProps {
    searchUrl: string;
    searchScope: StartNewSearchScope;
}

interface AccommodationDataInterface {
    view: HotelListView;
    isLoading: boolean;
    locationFilterData: LocationFilterData;
    accommodationList: HotelList;
    alternativeHotelList: HotelList;
    responseStatus: ResponseStatus;
    serviceInformation?: ServiceInformation;
    actionMap: ActionMap;
    lastClickedHotel?: {
        id: number;
        name: string;
        isFeaturedHotel: boolean;
    };
    searchStatus: StartNewSearchScope | null;
    tracking?: Tracking;
    transactionId?: string;
}

export const AccommodationDataContext =
    createContext<AccommodationDataInterface>({
        view: HotelListView.FakeTiles,
        isLoading: false,
        accommodationList: emptyHotelList,
        alternativeHotelList: emptyHotelList,
        responseStatus: ResponseStatus.Empty,
        serviceInformation: ServiceInformation.fromPartial({}),
        actionMap: buildActionMap(() => {}),
        searchStatus: null,
        locationFilterData: {},
    });

const validResponseStatus = new Set([
    ResponseStatus.Success,
    ResponseStatus.Empty,
    ResponseStatus.Pending,
]);

export const AccommodationDataProvider: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const abortController = useRef<AbortController>();
    const activeFilterListRef = useRef<ActiveFilter[]>([]);
    const searchUrlRef = useRef('');
    const searchStartTime = useRef(0);
    const cacheHitRef = useRef(CacheHit.Miss);
    const initialRequestRef = useRef(false);
    const initialSearchRef = useRef(true);
    const pendingFilterInformation = useRef<FilterInformation>();
    const namespace = useContext(CommunicationContext);
    const searchDData = useContext(SearchDContext);
    const { updateActiveFilterList } = useContext(SearchFormActionContext);
    const { updateTravelExpertList, updateChatBotData, resetTravelExpertData } =
        useContext(TravelExpertActionContext);
    const { setTransactionId } = useContext(InternalOutputActionContext);

    const { updateSearchMeta } = useSearch();
    const setMetaInformation = useSetAtom(metaAtom);
    const updateAccommodationData = useUpdateAccommodation();
    const addDebugBarData = useAddDebugBarEntry();
    const saveLastSearch = useSaveLastSearch();

    const { react } = namespace;
    const [isTravelExpertsChatbot, isDeviceDActive] = useFeatureList(
        Feature.TravelExpertsChatBot,
        Feature.S3HotelListDeviced
    );
    const { t } = useTranslation();

    const hotelListDynamicController =
        namespace.components.travelsearch.hotellistDynamicController;

    const [
        {
            responseData,
            requestData,
            view,
            isLoading,
            pollingUrl,
            pollingFinished,
            lastClickedHotel,
            searchStatus,
        },
        dispatch,
    ] = useReducer(AccommodationListReducer, initialState);

    const actionMap = useMemo(() => buildActionMap(dispatch), []);

    const contextValue = useMemo<AccommodationDataInterface>(
        () => ({
            view,
            isLoading,
            accommodationList: responseData.hotelList ?? emptyHotelList,
            alternativeHotelList:
                responseData.alternativeHotelList ?? emptyHotelList,
            locationFilterData: buildLocationFilterData(
                responseData.cityList,
                responseData.regionList
            ),
            responseStatus: responseData.status,
            serviceInformation: responseData.serviceInformation,
            actionMap,
            lastClickedHotel,
            searchStatus,
            tracking: responseData.tracking,
            transactionId: requestData.transactionId,
        }),
        [
            view,
            isLoading,
            responseData.hotelList,
            responseData.alternativeHotelList,
            responseData.status,
            responseData.serviceInformation,
            responseData.tracking,
            responseData.cityList,
            responseData.regionList,
            actionMap,
            lastClickedHotel,
            searchStatus,
            requestData.transactionId,
        ]
    );

    // fire list requests on requestData change
    useEffect(() => {
        const fetchList = async () => {
            try {
                initialRequestRef.current = requestData.isInitialRequest;
                const response = await fetchAccommodationList(
                    requestData,
                    abortController.current,
                    isDeviceDActive
                );

                addDebugBarData(
                    ApiEndpoint.HotelList,
                    response.debugBar?.externalCalls
                );

                if (requestData.isInitialRequest) {
                    if (response.status !== ResponseStatus.Pending) {
                        cacheHitRef.current = CacheHit.Hit;
                    } else if (
                        !!response?.hotelList?.itemOrder.length ||
                        !!response?.alternativeHotelList?.itemOrder.length
                    ) {
                        cacheHitRef.current = CacheHit.Partial;
                    } else {
                        cacheHitRef.current = CacheHit.Miss;
                    }
                }

                const redirectUrl = response?.hotelList?.meta?.redirectUrl;
                if (redirectUrl && redirectUrl.length > 5) {
                    // 5, random number, to verify that is url not empty
                    document.location.href = redirectUrl;

                    return;
                }

                Console.info('hotel list response:', response);
                actionMap.updateAccommodationList(response);
                if (response.hotelList) {
                    updateAccommodationData(response.hotelList.items);
                }
                if (response.alternativeHotelList) {
                    updateAccommodationData(
                        response.alternativeHotelList.items
                    );
                }
                if (
                    requestData.isInitialRequest &&
                    !response.travelExpertList
                ) {
                    resetTravelExpertData();
                } else {
                    response.travelExpertList &&
                        updateTravelExpertList(response.travelExpertList);
                }
            } catch (e) {
                if (e instanceof Error && e.message === 'aborted') {
                    return;
                }
                Console.error(e);
                actionMap.updateAccommodationList(
                    HotelListResponse.fromPartial({
                        status: ResponseStatus.Error,
                    })
                );
            }
        };
        if (requestData.clientId.length) {
            setTransactionId(requestData.transactionId);
            fetchList();
        }
        //IMPORTANT: except of requestData only static dependencies are allowed to prevent unexpected hotel list calls
    }, [
        actionMap,
        requestData,
        updateTravelExpertList,
        setTransactionId,
        resetTravelExpertData,
        isDeviceDActive,
        updateAccommodationData,
        addDebugBarData,
    ]);

    // start polling on pollingUrl change
    useEffect(() => {
        if (pollingUrl.length && searchDData && !pollingFinished) {
            startPolling(
                pollingUrl,
                searchDData,
                actionMap,
                abortController.current
            );
        }
    }, [actionMap, pollingFinished, pollingUrl, searchDData]);

    useEffect(() => {
        if (
            !isLoading &&
            responseData.status !== ResponseStatus.Pending &&
            hotelListDynamicController?.api
        ) {
            hotelListDynamicController.api.onSearchFinished();
        }
    }, [hotelListDynamicController?.api, isLoading, responseData.status]);

    // push legacy updates
    useEffect(() => {
        if (!hotelListDynamicController?.api) {
            return;
        }
        const accommodationResponseForMap =
            mapAccommodationListResponseForLegacy(t, responseData);
        const destination = searchStore.get(destinationAtom);
        hotelListDynamicController.api.onMapData(
            accommodationResponseForMap.mapDataList ??
                MapDataList.fromPartial({}),
            false,
            searchUrlRef.current,
            accommodationResponseForMap.hotelList,
            getDestinationId(destination),
            destination.label
        );
        hotelListDynamicController.api.onDestinationData({
            cityList: responseData.cityList,
            regionList: responseData.regionList,
            serviceInformation: responseData.serviceInformation,
            historyData: responseData.hotelList?.meta?.historyData,
            hasOffers: !!responseData.hotelList?.count,
            autocompleterDestinationName:
                responseData.hotelList?.meta?.autoCompleterDestinationName ??
                '',
            status: responseData.status,
        });
        if (
            responseData.status === ResponseStatus.Success &&
            responseData?.hotelList &&
            responseData.hotelList.itemOrder.length > 0 &&
            Object.keys(responseData.hotelList.items).length > 0
        ) {
            if (
                responseData.hotelList?.meta?.autoCompleterDestinationName ||
                destination.label
            ) {
                saveLastSearch(
                    responseData.hotelList.meta?.autoCompleterDestinationName ??
                        destination.label ??
                        ''
                );
            }
            hotelListDynamicController.api.onSaveLastSearchUrlData({
                hotelList: responseData.hotelList,
                searchUrl: searchUrlRef.current,
            });
        }
        if (responseData?.filterList) {
            // reducer Action
            hotelListDynamicController.api.onFilterData({
                filterList: responseData.filterList,
                activeFilterList: activeFilterListRef.current,
                isFinished: responseData.status !== ResponseStatus.Pending,
                hotelCount: responseData.hotelList?.count ?? 0,
            });
        }
    }, [hotelListDynamicController, responseData, saveLastSearch, t]);

    // update travelTicker
    useEffect(() => {
        if (responseData?.travelTickerList && hotelListDynamicController?.api) {
            hotelListDynamicController.api.onTravelTickerData({
                travelTickerList: responseData.travelTickerList.items,
            });
        }
    }, [hotelListDynamicController, responseData.travelTickerList]);

    // update chat bot data
    useEffect(() => {
        if (isTravelExpertsChatbot) {
            const handleChatBotData = async () => {
                if (responseData.serviceInformation?.code) {
                    const chatBotResponse = await fetchChatBotData(
                        responseData.serviceInformation?.code
                    );

                    chatBotResponse.chatBot &&
                        updateChatBotData(chatBotResponse.chatBot);
                }
            };
            handleChatBotData();
        }
    }, [
        responseData.serviceInformation?.code,
        isTravelExpertsChatbot,
        updateChatBotData,
    ]);

    useEffect(() => {
        if (
            responseData.status !== ResponseStatus.Pending &&
            responseData.status !== ResponseStatus.ResponseStatusUnknown
        ) {
            if (searchStartTime.current) {
                trackStatsDHotelListTiming(
                    Date.now() - searchStartTime.current
                );
            }
            if (pendingFilterInformation.current) {
                setMetaInformation(pendingFilterInformation.current);
                pendingFilterInformation.current = undefined;
            }
            trackStatsDHotelListCacheHit(cacheHitRef.current);
            trackStatsDHotelList(responseData.status, view);
            if (
                responseData.status === ResponseStatus.Empty ||
                (!responseData.hotelList?.itemOrder.length &&
                    !responseData.alternativeHotelList?.itemOrder.length)
            ) {
                trackNoResult(initialSearchRef.current);
            }
            initialSearchRef.current = false;
        }
    }, [
        responseData.alternativeHotelList?.itemOrder.length,
        responseData.hotelList?.itemOrder.length,
        responseData.status,
        setMetaInformation,
        view,
    ]);

    useEffect(() => {
        const onUnload = () => {
            abortController.current?.abort();
        };

        const getLegacySearchScope = (data: SearchInterface) => {
            if (!data.isUserInteraction) {
                return StartNewSearchScope.Initial;
            } else if (
                data.variables.isNewPagingPage &&
                !data.destinationChangeInformation.isNewSearchRegion
            ) {
                return StartNewSearchScope.Pagination;
            } else if (data.destinationChangeInformation.isNewSearchRegion) {
                return StartNewSearchScope.Destination;
            }
            return StartNewSearchScope.Default;
        };

        const fetchActiveFilterList_ = async (searchUrl: string) => {
            const activeFilterResponse = await fetchActiveFilterList(
                searchUrl,
                abortController.current
            );
            if (activeFilterResponse.status === ResponseStatus.Success) {
                updateActiveFilterList(activeFilterResponse.activeFilterList);
                activeFilterListRef.current =
                    activeFilterResponse.activeFilterList;
            }
        };

        const handleSearch = ({
            searchUrl,
            searchScope,
        }: HandleSearchProps) => {
            searchStartTime.current = Date.now();
            // cancel previous calls on new search
            abortController.current?.abort();
            // clear pending filterinformation
            pendingFilterInformation.current = undefined;

            try {
                abortController.current = new AbortController();

                actionMap.startNewSearch(searchUrl, searchScope);
            } catch {
                actionMap.updateAccommodationList(
                    HotelListResponse.fromPartial({
                        status: ResponseStatus.Error,
                    })
                );
            }

            searchUrlRef.current = searchUrl;
            namespace.components.travel.serviceCodeFunc?.resetServiceCode();
            fetchActiveFilterList_(searchUrl);
        };

        const handleLegacySearch = (data: SearchInterface) => {
            searchStartTime.current = Date.now();
            const defaultSearchUrl = data.variables.previousSearchRequestUrl;
            Console.info('data event', data);
            // check whether request is initial or not
            const scope = getLegacySearchScope(data);
            handleSearch({
                searchUrl: defaultSearchUrl,
                searchScope: scope,
            });
        };

        window.addEventListener('beforeunload', onUnload);
        react.bind('hotel-list-start-search', handleLegacySearch);
        react.bind('hotel-list-search', handleSearch); // todo: maybe better event-name :)
        return () => {
            window.removeEventListener('beforeunload', onUnload);
            react.unbind('hotel-list-start-search', handleLegacySearch);
            react.unbind('hotel-list-search', handleSearch);
        };
    }, [
        actionMap,
        namespace.components.travel.serviceCodeFunc,
        react,
        setMetaInformation,
        updateActiveFilterList,
    ]);

    // set necessary data for atom-search
    useEffect(() => {
        if (validResponseStatus.has(responseData.status)) {
            let compoundHotelListItemCount = 0;

            if (responseData.alternativeHotelList) {
                compoundHotelListItemCount +=
                    responseData.alternativeHotelList.count;
            }

            if (responseData.hotelList) {
                compoundHotelListItemCount += responseData.hotelList.count;

                const pageCount = Math.ceil(
                    compoundHotelListItemCount / defaultCountPerPage
                );
                updateSearchMeta(
                    pageCount,
                    initialRequestRef.current
                        ? responseData.hotelList.meta
                              ?.autoCompleterDestinationName
                        : undefined
                );
            }

            if (responseData.filterInformation) {
                const filterInformation = responseData.filterInformation;
                if (
                    responseData.status !== ResponseStatus.Pending ||
                    (initialSearchRef.current && initialRequestRef.current)
                ) {
                    setMetaInformation(responseData.filterInformation);
                    pendingFilterInformation.current = undefined;
                } else {
                    // just update tour operator list but keep complete filter information for later purposes
                    pendingFilterInformation.current = filterInformation;
                    setMetaInformation((current) => ({
                        ...current,
                        tourOperator: filterInformation.tourOperator,
                    }));
                }
            }
        }
    }, [
        responseData.alternativeHotelList,
        responseData.filterInformation,
        responseData.hotelList,
        responseData.status,
        setMetaInformation,
        updateSearchMeta,
    ]);

    return (
        <AccommodationDataContext.Provider value={contextValue}>
            {children}
        </AccommodationDataContext.Provider>
    );
};
